import React from "react";
import { Baby, BookOpen, GraduationCap, Sparkles, Palette, Calculator, Globe, Beaker, Music } from "lucide-react";
import { ASSETS } from "@/lib/assets";

const CYCLES = [
  {
    icon: Baby, color: "#FBBF24", bg: "bg-[#FBBF24]/10",
    name: "Maternelle", age: "3 à 5 ans", levels: ["Petite Section", "Moyenne Section", "Grande Section"],
    desc: "Éveil, socialisation et préparation aux apprentissages fondamentaux à travers le jeu et la créativité.",
    matieres: ["Langage", "Motricité", "Arts plastiques", "Découverte du monde", "Comptines & chants"],
  },
  {
    icon: BookOpen, color: "#166534", bg: "bg-[#166534]/10",
    name: "Primaire", age: "6 à 11 ans", levels: ["CP1", "CP2", "CE1", "CE2", "CM1", "CM2"],
    desc: "Maîtrise des savoirs fondamentaux : lecture, écriture, calcul et ouverture culturelle.",
    matieres: ["Français", "Mathématiques", "Sciences", "Histoire-Géo", "Éducation civique", "Anglais", "Sport"],
  },
  {
    icon: GraduationCap, color: "#1E3A8A", bg: "bg-[#1E3A8A]/10",
    name: "Collège", age: "12 à 15 ans", levels: ["6ème", "5ème", "4ème", "3ème"],
    desc: "Approfondissement des connaissances et préparation au Brevet d'Études du Premier Cycle (BEPC).",
    matieres: ["Français", "Mathématiques", "PC", "SVT", "Histoire-Géo", "Anglais", "Arabe", "EPS"],
  },
  {
    icon: Sparkles, color: "#EA580C", bg: "bg-[#EA580C]/10",
    name: "Lycée", age: "16 à 19 ans", levels: ["Seconde", "Première", "Terminale"],
    desc: "Spécialisation par filière (Sciences, Sciences Sociales, Lettres) et préparation au Baccalauréat.",
    matieres: ["Maths", "Physique-Chimie", "SVT", "Philosophie", "Histoire-Géo", "Anglais", "Économie"],
  },
];

const PARASCOLAIRES = [
  { icon: Palette, name: "Arts plastiques", color: "text-[#EA580C]" },
  { icon: Calculator, name: "Club Mathématiques", color: "text-[#166534]" },
  { icon: Globe, name: "Géopolitique junior", color: "text-[#1E3A8A]" },
  { icon: Beaker, name: "Laboratoire & sciences", color: "text-[#FBBF24]" },
  { icon: Music, name: "Chorale & musique", color: "text-[#EA580C]" },
  { icon: BookOpen, name: "Bibliothèque", color: "text-[#166534]" },
];

export default function Cycles() {
  return (
    <div className="bg-white" data-testid="page-cycles">
      <section className="relative bg-[#1E3A8A] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={ASSETS.building} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A]/90 via-[#1E3A8A]/80 to-[#166534]/70" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="chip bg-white/10 border-white/20 text-[#FBBF24] mb-4"><span>Cycles & programmes</span></div>
          <h1 className="mazid-headline text-5xl sm:text-6xl lg:text-7xl mb-6 max-w-4xl">
            De la maternelle au baccalauréat, <span className="text-[#FBBF24]">un parcours d&apos;excellence</span>.
          </h1>
          <p className="text-lg text-slate-200 max-w-3xl leading-relaxed">
            Quatre cycles complets, des enseignants dédiés et un programme rigoureux conforme aux standards nationaux et internationaux.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-6">
          {CYCLES.map((c, i) => (
            <div key={i} className="mazid-card p-7" data-testid={`cycle-card-${i}`}>
              <div className="flex items-start justify-between mb-5">
                <div className={`w-14 h-14 rounded-xl ${c.bg} flex items-center justify-center`}>
                  <c.icon className="h-7 w-7" style={{ color: c.color }} strokeWidth={1.6} />
                </div>
                <span className="chip">{c.age}</span>
              </div>
              <h2 className="font-heading font-extrabold text-3xl text-[#1E3A8A] mb-3">{c.name}</h2>
              <p className="text-slate-600 leading-relaxed mb-5">{c.desc}</p>
              <div className="mb-4">
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Niveaux</div>
                <div className="flex flex-wrap gap-2">
                  {c.levels.map((l, j) => (
                    <span key={j} className="text-xs px-3 py-1.5 rounded-md bg-slate-100 text-slate-700 font-medium">{l}</span>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Matières principales</div>
                <div className="flex flex-wrap gap-2">
                  {c.matieres.map((m, j) => (
                    <span key={j} className="text-xs px-3 py-1.5 rounded-md border border-[#166534]/20 text-[#166534] font-medium">{m}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC] border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="chip mb-4"><span>Activités parascolaires</span></div>
          <h2 className="font-heading text-4xl font-extrabold text-[#1E3A8A] tracking-tight mb-12 max-w-3xl">
            L&apos;épanouissement au-delà des classes.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {PARASCOLAIRES.map((p, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-slate-200 hover:border-[#166534] transition-colors" data-testid={`parascolaire-${i}`}>
                <p.icon className={`h-6 w-6 ${p.color} mb-3`} strokeWidth={1.6} />
                <div className="text-sm font-semibold text-[#0F172A]">{p.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
